import { useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// Basic iOS Safari detection that also covers iPadOS 13+ (desktop UA with touch).
export function isIosSafari(userAgent?: string): boolean {
  if (typeof navigator === "undefined") {
    return false;
  }

  const ua = (userAgent ?? navigator.userAgent ?? "").toLowerCase();
  const hasTouchWindow = typeof window !== "undefined" && "ontouchend" in window;
  const isAppleMobile = /iphone|ipod|ipad/.test(ua) ||
    (ua.includes("macintosh") && hasTouchWindow);
  const isWebkit = ua.includes("safari") && !ua.includes("crios") && !ua.includes("fxios");

  return isAppleMobile && isWebkit;
}

export function supportsNativeFullscreen(): boolean {
  if (typeof document === "undefined") {
    return false;
  }
  const doc = document as Document & {
    webkitFullscreenEnabled?: boolean;
    mozFullScreenEnabled?: boolean;
    msFullscreenEnabled?: boolean;
  };
  return Boolean(
    doc.fullscreenEnabled ??
      doc.webkitFullscreenEnabled ??
      doc.mozFullScreenEnabled ??
      doc.msFullscreenEnabled
  );
}

export function useFullscreenMode() {
  const location = useLocation();
  const navigate = useNavigate();

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const isFullscreenRoute = searchParams.has("fullscreen");

  const enterFullscreenRoute = useCallback(() => {
    const params = new URLSearchParams(location.search);
    params.set("fullscreen", "1");
    const search = params.toString();
    navigate(`${location.pathname}?${search}`, { replace: false });
  }, [location.pathname, location.search, navigate]);

  const exitFullscreenRoute = useCallback(() => {
    const params = new URLSearchParams(location.search);
    params.delete("fullscreen");
    const search = params.toString();
    const target = search.length ? `${location.pathname}?${search}` : location.pathname;
    navigate(target, { replace: true });
  }, [location.pathname, location.search, navigate]);

  return {
    isFullscreenRoute,
    enterFullscreenRoute,
    exitFullscreenRoute,
    isIosSafari: isIosSafari(),
    supportsNativeFullscreen: supportsNativeFullscreen(),
  };
}
